import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import type { EscalationPolicySummary } from "../lib/escalation.js";

/**
 * Tenant admin — Escalation Policies list (ADR-0042 §8.4).
 *
 * Route: `/app/admin/escalation-policies`
 *
 * Lists all policies (active + inactive). Create, edit, deactivate,
 * and delete each land on the editor route at
 * `/app/admin/escalation-policies/:id` (id = "new" for a fresh one).
 *
 * Delete is soft-blocked server-side with 409 when any open action item
 * still references the policy. We translate that into a friendly message
 * and recommend deactivation instead.
 */
export function AdminEscalationPolicies() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [policies, setPolicies] = useState<EscalationPolicySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ policies: EscalationPolicySummary[] }>(
        "/api/tenant/admin/escalation-policies"
      );
      setPolicies(res.policies);
      setBusy({});
      setRowError({});
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggleActive(p: EscalationPolicySummary) {
    setBusy((prev) => ({ ...prev, [p.id]: true }));
    setRowError((prev) => ({ ...prev, [p.id]: "" }));
    try {
      await apiFetch(`/api/tenant/admin/escalation-policies/${p.id}`, {
        method: "PATCH",
        body: { active: !p.active }
      });
      toast.push({
        kind: "success",
        message: p.active ? "Policy deactivated." : "Policy reactivated."
      });
      await load();
    } catch (e) {
      setRowError((prev) => ({
        ...prev,
        [p.id]: e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e)
      }));
    } finally {
      setBusy((prev) => ({ ...prev, [p.id]: false }));
    }
  }

  async function deletePolicy(p: EscalationPolicySummary) {
    const ok = await confirm({
      title: `Delete "${p.name}"?`,
      detail:
        "If this policy is referenced by any open action item, the hub will block the delete. " +
        "Deactivate the policy instead to stop it from being attached to new items while keeping history.",
      confirmLabel: "Delete"
    });
    if (!ok) return;
    setBusy((prev) => ({ ...prev, [p.id]: true }));
    setRowError((prev) => ({ ...prev, [p.id]: "" }));
    try {
      await apiFetch(`/api/tenant/admin/escalation-policies/${p.id}`, { method: "DELETE" });
      toast.push({ kind: "success", message: "Policy deleted." });
      await load();
    } catch (e) {
      const msg =
        e instanceof ApiRequestError
          ? e.code === "policy_in_use"
            ? "This policy is referenced by at least one open action item. Deactivate it or resolve the item first."
            : e.message
          : e instanceof Error
          ? e.message
          : String(e);
      setRowError((prev) => ({ ...prev, [p.id]: msg }));
    } finally {
      setBusy((prev) => ({ ...prev, [p.id]: false }));
    }
  }

  return (
    <PageLayout title="Escalation Policies">
      <div style={{ display: "grid", gap: 12 }}>
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
            <div style={{ marginLeft: "auto" }}>
              <Link className="btn btnPrimary" to="/app/admin/escalation-policies/new">
                + New policy
              </Link>
            </div>
          </div>

          {error && <ErrorState detail={error} />}
          {loading && !error && <LoadingState />}
          {!loading && !error && policies.length === 0 && (
            <div className="adminListEmpty">
              No escalation policies yet. Create one to attach it to watch-generated action items.
            </div>
          )}
          {!loading && !error && policies.length > 0 && (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Tiers</th>
                    <th>Status</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {policies.map((p) => {
                    const rowBusy = busy[p.id] ?? false;
                    const err = rowError[p.id];
                    return (
                      <tr key={p.id}>
                        <td>
                          <div style={{ fontWeight: 600 }}>
                            <Link to={`/app/admin/escalation-policies/${p.id}`}>{p.name}</Link>
                          </div>
                          {p.description && (
                            <div className="muted" style={{ marginTop: 2, fontSize: 13 }}>
                              {p.description}
                            </div>
                          )}
                        </td>
                        <td className="muted">
                          {p.tiers_count === 0
                            ? <span className="muted">No tiers</span>
                            : p.tiers_summary}
                        </td>
                        <td>
                          {p.active ? (
                            <span className="pill pillOk">active</span>
                          ) : (
                            <span className="pill pillBad">inactive</span>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {err && (
                            <div className="help" style={{ color: "var(--danger-text)", marginBottom: 4 }}>
                              {err}
                            </div>
                          )}
                          <div className="adminListActions">
                            <Link
                              className="adminListAction"
                              to={`/app/admin/escalation-policies/${p.id}`}
                            >
                              Edit
                            </Link>
                            <button
                              className="adminListAction"
                              onClick={() => void toggleActive(p)}
                              disabled={rowBusy}
                              type="button"
                            >
                              {p.active ? "Deactivate" : "Reactivate"}
                            </button>
                            <button
                              className="adminListAction isDanger"
                              onClick={() => void deletePolicy(p)}
                              disabled={rowBusy}
                              type="button"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
